import { useState, useEffect, useMemo } from 'react';
import type { Wall, Point, ElementType } from '../types';
import { analyzeWallSegmentation, findSegmentAtPoint, type WallSegment } from '../utils/wallSegmentation';

interface SegmentHighlight {
  id: string;
  start: Point;
  end: Point;
  isHovered: boolean;
  intersectionPoints: Point[];
}

export function useWallSegmentation(
  walls: Wall[],
  currentTool: ElementType,
  mousePosition: Point | null,
  scale: number
) {
  const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(null);
  
  // Check if we're in wall type mode
  const isWallTypeMode = currentTool === 'hlp' || currentTool === 'pw' || currentTool === 'sw';
  
  // Analyze wall segmentation when in wall type mode
  const segmentationResult = useMemo(() => {
    if (!isWallTypeMode || walls.length === 0) {
      return null;
    }
    
    console.log('🔧 SEGMENTATION HOOK: Analyzing walls for segmentation', {
      wallCount: walls.length,
      tool: currentTool
    });
    
    return analyzeWallSegmentation(walls);
  }, [walls, isWallTypeMode]); // Remove currentTool as it's already captured in isWallTypeMode

  // Create segment highlights for rendering
  const segmentHighlights = useMemo((): SegmentHighlight[] | null => {
    if (!segmentationResult || !isWallTypeMode) {
      return null;
    }

    // Only show segments that have intersections (can be split)
    const segmentsWithIntersections = segmentationResult.segments.filter(
      segment => segment.intersectionPoints.length > 0 || segment.segmentIndex > 0
    );

    if (segmentsWithIntersections.length === 0) {
      return null;
    }

    return segmentsWithIntersections.map(segment => ({
      id: segment.id,
      start: segment.start,
      end: segment.end,
      isHovered: segment.id === hoveredSegmentId,
      intersectionPoints: segment.intersectionPoints
    }));
  }, [segmentationResult, isWallTypeMode, hoveredSegmentId]);

  // Update hovered segment based on mouse position (with throttling)
  useEffect(() => {
    if (!segmentationResult || !mousePosition || !isWallTypeMode) {
      setHoveredSegmentId(null);
      return;
    }

    // Throttle mouse hover detection to improve performance
    const timeoutId = setTimeout(() => {
      const tolerance = 15 / scale; // Scale-aware tolerance
      const hoveredSegment = findSegmentAtPoint(mousePosition, segmentationResult.segments, tolerance);
      
      setHoveredSegmentId(hoveredSegment?.id || null);
    }, 16); // ~60fps throttling

    return () => clearTimeout(timeoutId);
  }, [segmentationResult, mousePosition, isWallTypeMode, scale]);

  // Clear hover state when leaving wall type mode
  useEffect(() => {
    if (!isWallTypeMode) {
      setHoveredSegmentId(null);
    }
  }, [isWallTypeMode]);

  return {
    segmentationResult,
    segmentHighlights,
    hoveredSegmentId,
    isWallTypeMode
  };
} 